import { cn } from "@/lib/utils";

/**
 * Žiedas — the Scio mark.
 *
 * It is the proficiency ring the app already draws for every skill, filled to
 * exactly 70: the number at which a prerequisite unlocks the next skill
 * (SkillPriority::MASTERED_AT). Drawing it at any other value would make the
 * mark a decoration rather than the thing it depicts.
 *
 * The ring takes `currentColor`, so it inherits from whatever it sits in
 * (`text-primary` in the sidebar, `text-primary-foreground` on a filled
 * surface). Only the centre dot is fixed, to `--brand-accent` — the one warm
 * note in the palette, reserved for what a learner has earned.
 *
 * The same geometry is drawn in four other places: `public/icon.svg` (the
 * browser tab, colours baked in), and `public/mark.svg` / `mark-missing.svg` /
 * `mark-broken.svg` (the static error pages, which cannot reach a React
 * component or a CSS variable). "Keep them in step" used to be this sentence and
 * nothing else, and it did not hold — the error pages shipped with the arc in
 * --brand-accent and no centre dot at all. `spec/content/scio_mark_spec.rb` now
 * holds all five to one radius, weight, dot and 70 fill.
 */
export function ScioMark({
  className,
  size = 24,
  title,
}: {
  className?: string;
  size?: number;
  /** Give this only where the mark is the sole naming of the app; beside the
   *  word "Scio" it is decorative and must stay unlabelled. */
  title?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      className={cn("shrink-0", className)}
      role={title ? "img" : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
    >
      {title ? <title>{title}</title> : null}
      <circle
        cx="24"
        cy="24"
        r="17"
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.2"
        strokeWidth="6"
      />
      {/* r=17 → circumference 106.81; 74.8 of it is 70%. */}
      <circle
        cx="24"
        cy="24"
        r="17"
        fill="none"
        stroke="currentColor"
        strokeWidth="6"
        strokeLinecap="round"
        strokeDasharray="74.8 106.8"
        transform="rotate(-90 24 24)"
      />
      <circle cx="24" cy="24" r="5" fill="var(--brand-accent)" />
    </svg>
  );
}
